import { useCallback, useRef } from 'react'
import { ColumnMovedEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import type { RefObject } from 'react'

export function useColumnOrderPersistence<TData>(
  gridRef: RefObject<AgGridReact<TData>>,
  storageKey: string
) {
  const isRestoringRef = useRef(false)

  const saveColumnOrder = useCallback(() => {
    if (isRestoringRef.current) return
    if (!gridRef.current?.api) return
    const state = gridRef.current.api.getColumnState()
    const order = state.map((col) => col.colId)
    localStorage.setItem(storageKey, JSON.stringify(order))
  }, [gridRef, storageKey])

  const restoreColumnOrder = useCallback(() => {
    if (!gridRef.current?.api) return
    try {
      const saved = localStorage.getItem(storageKey)
      if (!saved) return
      const savedOrder: string[] = JSON.parse(saved)

      // Locked columns (e.g. selection + Benefit ID) must keep their original
      // definition order at the front and never be reordered by a saved layout.
      const currentState = gridRef.current.api.getColumnState()
      const lockedColIds: string[] = []
      for (const col of currentState) {
        const colDef = gridRef.current?.api?.getColumn(col.colId)?.getColDef()
        if (colDef?.lockPosition === true || colDef?.suppressMovable === true) {
          if (col.colId) lockedColIds.push(col.colId)
        }
      }
      const lockedSet = new Set(lockedColIds)

      const nextOrder = [
        ...lockedColIds,
        ...savedOrder.filter((colId) => !lockedSet.has(colId)),
      ]

      isRestoringRef.current = true
      gridRef.current.api.applyColumnState({
        state: nextOrder.map((colId) => ({ colId })),
        applyOrder: true,
      })
    } catch {
      // ignore corrupted saved state
    } finally {
      isRestoringRef.current = false
    }
  }, [gridRef, storageKey])

  const handleColumnMoved = useCallback(
    (event: ColumnMovedEvent<TData>) => {
      if (!event.finished) return
      saveColumnOrder()
    },
    [saveColumnOrder]
  )

  return { saveColumnOrder, restoreColumnOrder, handleColumnMoved }
}
